import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { ArrowRightLeft, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import type { IItemList } from '@/pages/ItemEyes/api/ItemEyes.api';
import { GetAllItems } from '@/pages/ItemEyes/api/ItemEyes.api';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { UpdateOneRequestItem } from '@/pages/PurchasesContainer/RequestPerOffer/api/OffersRequest.api';
import { LabelWithValue } from '@/UI/LabelWithTitle';

import { AutoCompleteItems } from './AllItemsSearch';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  item: IOneItemInPrepareForRequest | null;
  handleUpdateOneItemLink: (
    item: IOneItemInPrepareForRequest,
    skipResetPage?: boolean,
  ) => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
};

function EditItemRequestDialog({
  closeFn,
  isOpen,
  item,
  handleLoadingStage,
  isLoading,
  handleUpdateOneItemLink,
}: Props) {
  const [itemData, setItemData] = useState<null | IItemList>(null);
  const queryClient = useQueryClient();

  // Use the shared query for all items
  const { data: allItems = [] } = useQuery({
    queryKey: ['allItems'], // Same shared key as AutoCompleteItems
    queryFn: () => GetAllItems(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Find the initial item from the shared data
  const initialItem = React.useMemo(() => {
    if (item?.HealthyItemID && allItems.length > 0) {
      return allItems.find((x) => x?.ID === item?.HealthyItemID) || null;
    }
    return null;
  }, [item?.HealthyItemID, allItems]);

  const handleItemData = useCallback((newValue: IItemList | null) => {
    setItemData(newValue);
  }, []);

  const handleClose = useCallback(() => {
    setItemData(null);
    closeFn();
    // Move focus to body or a safe element to avoid aria-hidden focus issues
    setTimeout(() => {
      if (
        document.activeElement &&
        (document.activeElement as HTMLElement).getAttribute('aria-hidden') ===
          'true'
      ) {
        (document.body as HTMLElement).focus();
      }
    }, 0);
  }, [closeFn]);

  // Set initial item data when allItems loads
  useEffect(() => {
    if (initialItem && !itemData) {
      setItemData(initialItem);
    }
  }, [initialItem, itemData]);

  const updateItemLinkMutation = useMutation({
    mutationFn: UpdateOneRequestItem,
    onSuccess: async () => {
      // Invalidate relevant queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['Request'] }),
        queryClient.invalidateQueries({ queryKey: ['prepareOffers'] }),
      ]);

      // Update the local state by calling the parent's update handler
      if (item && itemData) {
        const updatedItem: IOneItemInPrepareForRequest = {
          ...item,
          HealthyItemID: itemData.ID,
          itemName: itemData.Name,
          Price: itemData.Price,
        };
        handleUpdateOneItemLink(updatedItem, true); // Skip page reset
      }

      handleLoadingStage(false, 'update');
      setItemData(null);
      toast.success('تم التعديل بنجاح');
      closeFn();
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء التعديل');
      handleLoadingStage(false, 'update');
    },
  });

  const handleUpdateItem = useCallback(async () => {
    if (!item || !itemData) {
      toast.error('يرجى اختيار صنف');
      return;
    }

    handleLoadingStage(true, 'update');

    try {
      await updateItemLinkMutation.mutateAsync({
        id: item.itemBodyID,
        itemID: itemData.ID,
      });
    } catch (error) {
      // Error is handled in mutation
      console.error('Update failed:', error);
    }
  }, [item, itemData, handleLoadingStage, updateItemLinkMutation]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            تعديل ربط الصنف
          </DialogTitle>
          <DialogDescription>
            اختر الصنف الجديد لربطه مع هذا العنصر
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Item Info */}
          <div className="rounded-lg border bg-gray-50/50 p-4 dark:bg-gray-800/50">
            <h4 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
              الصنف الحالي
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelWithValue
                  label="اسم الصنف"
                  value={item?.ClientItemName || 'غير محدد'}
                />
              </div>
              <div>
                <LabelWithValue
                  label="السعر"
                  value={`${initialItem?.Price || item?.Price || 0} ج.م`}
                />
              </div>
              <div>
                <LabelWithValue
                  label="الكمية"
                  value={item?.Qty?.toString() || '0'}
                />
              </div>
              <div>
                <LabelWithValue
                  label="الخصم"
                  value={`${item?.Discount || 0}%`}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* New Item Selection */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              اختر الصنف الجديد
            </h4>

            {allItems.length === 0 ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <AutoCompleteItems
                searchPlaceholder="ابحث عن صنف جديد..."
                value={itemData}
                onValueChange={handleItemData}
                isLoading={false} // No need to show loading here as allItems is fetched once
              />
            )}

            {/* Selected Item Details */}
            {itemData && (
              <div className="rounded-lg border bg-green-50/50 p-4 dark:bg-green-950/20">
                <h5 className="mb-2 font-medium text-green-900 dark:text-green-100">
                  الصنف المختار
                </h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <LabelWithValue label="اسم الصنف" value={itemData.Name} />
                  </div>
                  <div>
                    <LabelWithValue
                      label="السعر"
                      value={`${itemData.Price} ج.م`}
                    />
                  </div>
                  <div>
                    <LabelWithValue
                      label="الرقم التعريفي"
                      value={itemData.ID.toString()}
                    />
                  </div>
                  <div>
                    <Badge variant="soft" className="w-fit">
                      جاهز للربط
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            إلغاء
          </Button>
          <Button
            onClick={handleUpdateItem}
            disabled={!itemData || isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                جاري التحديث...
              </>
            ) : (
              <>
                <ArrowRightLeft className="h-4 w-4" />
                تحديث الربط
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EditItemRequestDialog;
